import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Skeleton,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { 
  format, 
  startOfWeek, 
  endOfWeek, 
  startOfMonth, 
  endOfMonth, 
  startOfYear, 
  endOfYear,
  addDays, 
  subDays, 
  addWeeks, 
  addMonths, 
  addYears,
  isSameDay, 
  isSameMonth, 
  isSameYear,
  isWithinInterval,
  isAfter,
  isBefore,
  getISODay,
  setMonth,
  startOfDay
} from "date-fns";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { get } from "../api/actions";
import { parseScheduleDateLocal } from "../utils/dateUtils";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

function CalendarView(props) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("week");
  const [selectedDate, setSelectedDate] = useState(null);
  const [filteredSchedules, setFilteredSchedules] = useState([]);

  useEffect(() => {
    const startDate = getViewStartDate();
    let endDate = getViewEndDate();
    
    if (viewMode === "week") {
      endDate = addDays(startDate, 7);
    }
    
    props.get({
      scheduledAfter: format(startDate, 'yyyy-MM-dd'),
      scheduledBefore: format(endDate, 'yyyy-MM-dd'),
      page: 1,
      limit: 10000,
    });
  }, [currentDate, viewMode]);

  useEffect(() => {
    const schedules = props.disconnectSchedules?.data?.disconnectSchedulesList?.items || [];
    setFilteredSchedules(schedules);
  }, [props.disconnectSchedules]);

  const getViewStartDate = () => {
    switch (viewMode) {
      case "week":
        return startOfWeek(currentDate, { weekStartsOn: 1 });
      case "month":
        return startOfMonth(currentDate);
      case "year":
        return startOfYear(currentDate);
      default:
        return startOfMonth(currentDate);
    }
  };

  const getViewEndDate = () => {
    switch (viewMode) {
      case "week":
        return endOfWeek(currentDate, { weekStartsOn: 1 });
      case "month":
        return endOfMonth(currentDate);
      case "year":
        return endOfYear(currentDate);
      default:
        return endOfMonth(currentDate);
    }
  };

  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  const navigateDate = (direction) => {
    let newDate = currentDate;
    switch (viewMode) {
      case "week":
        newDate = addWeeks(currentDate, direction);
        break;
      case "month":
        newDate = addMonths(currentDate, direction);
        break;
      case "year":
        newDate = addYears(currentDate, direction);
        break;
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const getSchedulesForDate = (date) => {
    return filteredSchedules.filter(schedule => {
      if (!schedule.scheduledDisconnectDate) return false;
      const parsed = parseScheduleDateLocal(schedule.scheduledDisconnectDate);
      const scheduleDateStr = parsed ? format(parsed, 'yyyy-MM-dd') : null;
      if (!scheduleDateStr) return false;
      const compareDateStr = format(startOfDay(date), 'yyyy-MM-dd');
      return scheduleDateStr === compareDateStr;
    });
  };

  const getSchedulesForWeek = () => {
    const startDate = startOfDay(getViewStartDate());
    const endDate = startOfDay(getViewEndDate());
    return filteredSchedules.filter(schedule => {
      if (!schedule.scheduledDisconnectDate) return false;
      const scheduleDate = parseScheduleDateLocal(schedule.scheduledDisconnectDate);
      if (!scheduleDate) return false;
      return isWithinInterval(scheduleDate, { start: startDate, end: endDate }) ||
             isSameDay(scheduleDate, startDate) ||
             isSameDay(scheduleDate, endDate);
    });
  };

  const renderMonthView = () => {
    const startDate = getViewStartDate();
    const endDate = getViewEndDate();
    const days = [];
    let current = new Date(startDate);
    const isLoading = props.disconnectSchedules?.isLoading || false;

    const firstDayOfWeek = getISODay(startDate);
    for (let i = 1; i < firstDayOfWeek; i++) {
      days.push(subDays(startDate, firstDayOfWeek - i));
    }

    // Add all days in the current month
    while (isSameDay(current, endDate) || isBefore(current, endDate)) {
      days.push(new Date(current));
      current = addDays(current, 1);
    }

    const lastDayOfWeek = getISODay(endDate);
    for (let i = lastDayOfWeek + 1; i <= 7; i++) {
      days.push(addDays(endDate, i - lastDayOfWeek));
    }

    return (
      <Box sx={{ width: '100%' }}>
        {/* Weekday headers - starting with Monday to match Weekly Disconnect Schedule */}
        <Box sx={{ display: 'flex', mb: 1 }}>
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
            <Box key={day} sx={{ flex: 1, textAlign: 'center', py: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                {day}
              </Typography>
            </Box>
          ))}
        </Box>
        
        {/* Calendar grid */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Create weeks */}
          {Array.from({ length: Math.ceil(days.length / 7) }, (_, weekIndex) => (
            <Box key={weekIndex} sx={{ display: 'flex', gap: 1 }}>
              {days.slice(weekIndex * 7, (weekIndex + 1) * 7).map((day, dayIndex) => {
                const daySchedules = getSchedulesForDate(day);
                const isCurrentMonth = isSameMonth(day, currentDate);
                const isToday = isSameDay(day, new Date());
                
                return (
                  <Box
                    key={dayIndex}
                    sx={{
                      flex: 1,
                      minHeight: 100,
                      maxHeight: 150,
                      backgroundColor: isCurrentMonth ? 'background.paper' : 'grey.50',
                      border: isToday ? 2 : 1,
                      borderColor: isToday ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      p: 1,
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      }
                    }}
                    onClick={() => setSelectedDate(day)}
                  >
                    {/* Date number */}
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: isToday ? 'bold' : 'normal',
                        color: isCurrentMonth ? 'text.primary' : 'text.secondary',
                        mb: 0.5,
                        fontSize: '0.875rem'
                      }}
                    >
                      {format(day, 'd')}
                    </Typography>
                    
                    <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', minHeight: 0 }}>
                      {isLoading ? (
                        <>
                          <Skeleton variant="rectangular" height={18} sx={{ mb: 0.25, borderRadius: 1 }} />
                          <Skeleton variant="rectangular" height={18} sx={{ mb: 0.25, borderRadius: 1 }} />
                        </>
                      ) : (
                        <>
                          {daySchedules.map((schedule, scheduleIndex) => (
                            <Chip
                              key={scheduleIndex}
                              label={schedule?.groupName || 'Unknown Group'}
                              size="small"
                              sx={{
                                backgroundColor: schedule?.bgColorTag ? `#${schedule.bgColorTag}` : 'primary.main',
                                color: 'white',
                                fontSize: '0.65rem',
                                height: 18,
                                mb: 0.25,
                                display: 'block',
                                width: '100%',
                                '& .MuiChip-label': {
                                  px: 0.5,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  fontSize: '0.65rem'
                                }
                              }}
                            />
                          ))}
                          {daySchedules.length === 0 && (
                            <Typography 
                              variant="caption" 
                              color="text.secondary" 
                              sx={{ fontSize: '0.6rem', display: 'block', mt: 0.25, fontStyle: 'italic' }}
                            >
                              No schedules
                            </Typography>
                          )}
                        </>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const renderWeekView = () => {
    const startDate = getViewStartDate();
    const days = [];
    const isLoading = props.disconnectSchedules?.isLoading || false;
    
    for (let i = 0; i < 7; i++) {
      days.push(addDays(startDate, i));
    }

    return (
      <Box sx={{ display: 'flex', gap: 1 }}>
        {days.map((day, index) => {
          const daySchedules = getSchedulesForDate(day);
          const isToday = isSameDay(day, new Date());
          
          return (
            <Box
              key={index}
              sx={{
                flex: 1,
                minHeight: 100,
                maxHeight: 150,
                backgroundColor: 'background.paper',
                border: isToday ? 2 : 1,
                borderColor: isToday ? 'primary.main' : 'divider',
                borderRadius: 1,
                p: 1,
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                '&:hover': {
                  backgroundColor: 'action.hover',
                }
              }}
              onClick={() => setSelectedDate(day)}
            >
              {/* Day name */}
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 'bold',
                  color: 'text.secondary',
                  mb: 0.5,
                  fontSize: '0.75rem',
                  textAlign: 'center'
                }}
              >
                {format(day, 'EEE')}
              </Typography>
              
              {/* Date number */}
              <Typography
                variant="body2"
                sx={{
                  fontWeight: isToday ? 'bold' : 'normal',
                  color: 'text.primary',
                  mb: 0.5,
                  fontSize: '0.875rem',
                  textAlign: 'center'
                }}
              >
                {format(day, 'd')}
              </Typography>
              
              <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', minHeight: 0 }}>
                {isLoading ? (
                  <>
                    <Skeleton variant="rectangular" height={18} sx={{ mb: 0.25, borderRadius: 1 }} />
                    <Skeleton variant="rectangular" height={18} sx={{ mb: 0.25, borderRadius: 1 }} />
                  </>
                ) : (
                  <>
                    {daySchedules.map((schedule, scheduleIndex) => (
                      <Chip
                        key={scheduleIndex}
                        label={schedule?.groupName || 'Unknown Group'}
                        size="small"
                        sx={{
                          backgroundColor: schedule?.bgColorTag ? `#${schedule.bgColorTag}` : 'primary.main',
                          color: 'white',
                          fontSize: '0.65rem',
                          height: 18,
                          mb: 0.25,
                          display: 'block',
                          width: '100%',
                          '& .MuiChip-label': {
                            px: 0.5,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: '0.65rem'
                          }
                        }}
                      />
                    ))}
                    {daySchedules.length === 0 && (
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        sx={{ fontSize: '0.6rem', display: 'block', mt: 0.25, textAlign: 'center', fontStyle: 'italic' }}
                      >
                        No schedules
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderYearView = () => {
    const months = [];
    const isLoading = props.disconnectSchedules?.isLoading || false;
    for (let i = 0; i < 12; i++) {
      months.push(setMonth(new Date(currentDate), i));
    }

    return (
      <Box sx={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 2,
      }}>
        {months.map((month, index) => {
          const monthSchedules = filteredSchedules.filter(schedule => {
            if (!schedule.scheduledDisconnectDate) return false;
            const scheduleDate = parseScheduleDateLocal(schedule.scheduledDisconnectDate);
            if (!scheduleDate) return false;
            return isSameMonth(scheduleDate, month);
          });
          
          return (
            <Box 
              key={index}
            >
              <Box 
                onClick={() => {
                  setCurrentDate(month);
                  setViewMode('month');
                }}
                sx={{
                  minHeight: 200,
                  height: "100%",
                  display: 'flex',
                  flexDirection: 'column',
                  backgroundColor: isSameMonth(month, new Date()) ? 'background.paper' : 'grey.50',
                  border: isSameMonth(month, new Date()) ? 2 : 1,
                  borderColor: isSameMonth(month, new Date()) ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  p: 1,
                  cursor: 'pointer',
                  overflow: 'hidden',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  }
                }}
              >
                  {/* Month name - matching month view date styling */}
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 'bold',
                      color: 'text.primary',
                      mb: 0.5,
                      fontSize: '0.875rem',
                      textAlign: 'center'
                    }}
                  >
                    {format(month, 'MMMM')}
                  </Typography>
                  
                  <Box sx={{ flex: 1, overflow: 'hidden' }}>
                    {isLoading ? (
                      <>
                        <Skeleton variant="text" width="60%" sx={{ mx: 'auto', mb: 0.5 }} />
                        <Skeleton variant="rectangular" height={18} sx={{ mb: 0.25, borderRadius: 1 }} />
                        <Skeleton variant="rectangular" height={18} sx={{ mb: 0.25, borderRadius: 1 }} />
                        <Skeleton variant="rectangular" height={18} sx={{ mb: 0.25, borderRadius: 1 }} />
                      </>
                    ) : (
                      <>
                        {monthSchedules.length === 0 ? (
                          <Typography 
                            variant="caption" 
                            color="text.secondary" 
                            sx={{ 
                              fontSize: '0.6rem', 
                              display: 'block', 
                              mt: 0.25,
                              textAlign: 'center',
                              fontStyle: 'italic'
                            }}
                          >
                            No schedules
                          </Typography>
                        ) : (
                          <>
                            <Typography
                              variant="caption" 
                              sx={{ 
                                mb: 0.5,
                                color: 'text.secondary',
                                fontSize: '0.6rem',
                                textAlign: 'center',
                                display: 'block'
                              }}
                            >
                              {monthSchedules.length} schedule{monthSchedules.length !== 1 ? 's' : ''}
                            </Typography>
                            
                            {monthSchedules.slice(0, 3).map((schedule, scheduleIndex) => (
                              <Chip
                                key={scheduleIndex}
                                label={schedule?.groupName || 'Unknown Group'}
                                size="small"
                                sx={{
                                  backgroundColor: schedule?.bgColorTag ? `#${schedule.bgColorTag}` : 'primary.main',
                                  color: 'white',
                                  fontSize: '0.65rem',
                                  height: 18,
                                  mb: 0.25,
                                  display: 'block',
                                  width: '100%',
                                  '& .MuiChip-label': {
                                    px: 0.5,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    fontSize: '0.65rem'
                                  }
                                }}
                              />
                            ))}
                            
                            {monthSchedules.length > 3 && (
                              <Typography 
                                variant="caption" 
                                color="text.secondary" 
                                sx={{ 
                                  fontSize: '0.6rem', 
                                  display: 'block', 
                                  mt: 0.25,
                                  textAlign: 'center'
                                }}
                              >
                                +{monthSchedules.length - 3}
                              </Typography>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  const getViewTitle = () => {
    switch (viewMode) {
      case "week":
        return `${format(getViewStartDate(), 'MMM d')} - ${format(getViewEndDate(), 'MMM d, yyyy')}`;
      case "month":
        return format(currentDate, 'MMMM yyyy');
      case "year":
        return format(currentDate, 'yyyy');
      default:
        return format(currentDate, 'MMMM yyyy');
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <CalendarTodayIcon sx={{ mr: 1 }} />
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Disconnect Schedule Calendar
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigateDate(-1)}>
              <ChevronLeftIcon />
            </IconButton>
            
            <Typography variant="h6" sx={{ minWidth: 200, textAlign: 'center' }}>
              {getViewTitle()}
            </Typography>
            
            <IconButton onClick={() => navigateDate(1)}>
              <ChevronRightIcon />
            </IconButton>
            
            <Button variant="outlined" onClick={goToToday}>
              Today
            </Button>
          </Box>

          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
          >
            <ToggleButton value="week">Week</ToggleButton>
            <ToggleButton value="month">Month</ToggleButton>
            <ToggleButton value="year">Year</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Calendar Content */}
        <Box sx={{ mt: 2 }}>
          {viewMode === "week" && renderWeekView()}
          {viewMode === "month" && renderMonthView()}
          {viewMode === "year" && renderYearView()}
        </Box>

        {/* Selected Date Details */}
        {selectedDate && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Schedules for {format(selectedDate, 'MMMM d, yyyy')}
            </Typography>
            {getSchedulesForDate(selectedDate).length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {getSchedulesForDate(selectedDate).map((schedule, index) => {
                  const scheduleDate = parseScheduleDateLocal(schedule.scheduledDisconnectDate);
                  const timeStr = scheduleDate ? format(scheduleDate, 'h:mm a') : '';
                  return (
                  <Chip
                    key={index}
                    label={`${schedule?.groupName || 'Unknown Group'}${timeStr ? ` - ${timeStr}` : ''}`}
                    sx={{
                      backgroundColor: schedule?.bgColorTag ? `#${schedule.bgColorTag}` : 'primary.main',
                      color: 'white',
                    }}
                  />
                  );
                })}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No schedules for this date
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
}

CalendarView.propTypes = {
  disconnectSchedules: PropTypes.object.isRequired,
  get: PropTypes.func.isRequired,
};

function State(state) {
  return {
    disconnectSchedules: state.disconnectSchedules,
  };
}

export default connect(State, { get })(CalendarView);
